/**
 * Modo Inspiração: a pessoa não sabe para onde ir de férias e pede uma
 * sugestão de destino (não um itinerário, não um ranking de locais).
 *
 * Regras duras:
 * - Só ativa quando NÃO há destino explícito no texto (senão é um pedido de
 *   rota normal para esse destino).
 * - Não ativa em pedidos de descoberta ("melhores francesinhas do Norte") —
 *   isso é o ramo `detectDiscoveryRequest`.
 * - Não ativa em pedidos com duração/logística ("tenho 4 horas no Porto").
 */

export type InspirationIntent = {
  /** Idioma detetado do pedido (melhor esforço; o idioma da UI manda). */
  language: "pt" | "en" | "es" | "fr" | "it" | "de";
  /** Forma forte de indecisão: ganha sempre a um "destino" extraído do texto. */
  strong: boolean;
  /** Duração já escrita no pedido ("3 dias"), se existir. */
  days: number | null;
};

type LangKey = InspirationIntent["language"];

// "me"/"moi"/"mi" pode vir com hífen, espaço ou nada: "diz-me", "diz me", "dizme".
const ME = "[-\\s]?me";

// Frases de indecisão / pedido de sugestão de destino, por idioma.
const INDECISION: Record<LangKey, RegExp> = {
  pt: new RegExp(
    `(n[aã]o sei para onde|n[aã]o sei o que (visitar|escolher)|para onde (devo|posso|vou) ir|(vou|vamos) para onde|onde (${ME} )?aconselh|diz(${ME})? um s[íi]tio|s[íi]tio para (ir de|tirar|passar) f[eé]rias|sugere(${ME})? um (destino|s[íi]tio|lugar)|sugira um (destino|s[íi]tio|lugar)|estou indeciso|estou indecisa|inspira(${ME})?\\b|ideias de destino|recomenda(${ME})? um destino)`,
    "i",
  ),
  en: /(don'?t know where to go|where should i go|where do you (recommend|suggest)|suggest (me )?a (destination|place)|recommend (me )?a (destination|place)|i'?m indecisive|inspire me|(holiday|vacation) ideas|anywhere (good|nice) to go)/i,
  es: /(no s[eé] (a )?d[oó]nde ir|d[oó]nde me (aconsejas|recomiendas)|sugi[eé]re(-?me)? un destino|recomi[eé]nda(-?me)? un destino|estoy indeciso|estoy indecisa|ideas de (destino|vacaciones)|insp[ií]rame)/i,
  fr: /(je ne sais pas o[uù] aller|o[uù] (me )?conseilles?-?tu|sugg[eè]re(-?moi)? une destination|recommande(-?moi)? une destination|je suis ind[eé]cis|id[eé]es de (destination|vacances)|inspire-?moi)/i,
  it: /(non so dove andare|dove mi consigli|consiglia(-?mi)? (una meta|una destinazione|un posto)|suggerisci(-?mi)? (una meta|una destinazione)|sono indeciso|sono indecisa|idee per (le )?vacanze|ispirami)/i,
  de: /(wei[ssß] nicht wohin|wohin soll ich|wohin (empfiehlst|reist) du|empfiehl( mir)? ein reiseziel|schlag( mir)? ein reiseziel vor|bin unentschlossen|urlaubsideen|inspirier mich)/i,
};

// Contexto de férias/viagem: exigido para as formas mais fracas ("inspira-me").
const TRAVEL_CONTEXT =
  /(f[eé]rias|viagem|viajar|viaje|vacaciones|vacances|holidays?|vacation|trip|travel|getaway|urlaub|reise|vacanze|viaggio|escapad)/i;

const STRONG: Record<LangKey, RegExp> = {
  pt: new RegExp(
    `(n[aã]o sei para onde|quero viajar mas n[aã]o sei|para onde (devo|vou) de f[eé]rias|onde aconselhas (ir|passar)|onde me aconselhas|diz(${ME})? um s[íi]tio para (ir|tirar|passar)|s[íi]tio para (ir de|tirar|passar) f[eé]rias)`,
    "i",
  ),
  en: /(don'?t know where to go|where should i go (on|for))/i,
  es: /(no s[eé] (a )?d[oó]nde ir|a d[oó]nde (voy|vamos) de vacaciones)/i,
  fr: /(je ne sais pas o[uù] aller|o[uù] partir en vacances)/i,
  it: /(non so dove andare (in|per le) vacan|non so dove andare)/i,
  de: /(wei[ssß] nicht wohin|wohin in den urlaub)/i,
};

const DURATION =
  /(\d{1,2})\s*(dias?|d[ií]as?|days?|jours?|giorni|tage|n[oó]ites?|nights?|noches?)\b/i;

function extractDays(text: string): number | null {
  const m = DURATION.exec(text);
  if (!m) return null;
  const n = Number.parseInt(m[1]!, 10);
  return Number.isFinite(n) && n >= 1 && n <= 30 ? n : null;
}

/**
 * Deteta pedidos do tipo "diz-me um sítio para ir de férias" /
 * "I don't know where to go on holiday". Devolve null se for outro ramo.
 */
export function detectInspirationRequest(text: string): InspirationIntent | null {
  const t = text.trim();
  if (t.length < 8 || t.length > 600) return null;

  for (const lang of Object.keys(INDECISION) as LangKey[]) {
    if (!INDECISION[lang].test(t)) continue;
    const days = extractDays(t);
    // Formas fortes bastam por si ("não sei para onde ir de férias").
    if (STRONG[lang].test(t)) return { language: lang, strong: true, days };
    // Formas fracas ("sugere um destino", "inspire me") precisam de contexto
    // de viagem/férias para não roubarem pedidos de outro tipo.
    if (TRAVEL_CONTEXT.test(t)) return { language: lang, strong: false, days };
  }
  return null;
}

/* -------- Copy multilingue da UI de perguntas -------- */

export type InspirationCopy = {
  title: string;
  subtitle: string;
  daysQuestion: string;
  vibeQuestion: string;
  budgetQuestion: string;
  anyOption: string;
  daysOptions: Array<{ days: number; label: string }>;
  vibeOptions: Array<{ vibe: "nature" | "beach" | "city" | "adventure" | "relax"; label: string }>;
  budgetOptions: Array<{ budget: "low" | "medium" | "high"; label: string }>;
  suggestButton: string;
  loading: string;
  noResults: string;
  dismiss: string;
};

const DAYS = [
  { days: 3, pt: "2–3 dias", en: "2–3 days", es: "2–3 días", fr: "2–3 jours", it: "2–3 giorni", de: "2–3 Tage", ja: "2〜3日" },
  { days: 5, pt: "4–5 dias", en: "4–5 days", es: "4–5 días", fr: "4–5 jours", it: "4–5 giorni", de: "4–5 Tage", ja: "4〜5日" },
  { days: 7, pt: "6–7 dias", en: "6–7 days", es: "6–7 días", fr: "6–7 jours", it: "6–7 giorni", de: "6–7 Tage", ja: "6〜7日" },
  { days: 10, pt: "8+ dias", en: "8+ days", es: "8+ días", fr: "8+ jours", it: "8+ giorni", de: "8+ Tage", ja: "8日以上" },
];

export function inspirationCopy(lang: string): InspirationCopy {
  const pick = <T,>(m: Record<string, T>): T => m[lang] ?? m.en;
  return {
    title: pick({
      pt: "Não sabes para onde ir? Vamos descobrir.",
      en: "Not sure where to go? Let's find out.",
      es: "¿No sabes a dónde ir? Vamos a descubrirlo.",
      fr: "Vous ne savez pas où aller ? Découvrons-le.",
      it: "Non sai dove andare? Scopriamolo.",
      de: "Du weißt nicht wohin? Finden wir es heraus.",
      ja: "行き先が決まっていませんか？一緒に見つけましょう。",
    }),
    subtitle: pick({
      pt: "Responde a 3 perguntas rápidas e sugerimos destinos reais e bonitos que pouca gente explora.",
      en: "Answer 3 quick questions and we'll suggest real, beautiful places few people explore.",
      es: "Responde a 3 preguntas rápidas y te sugerimos lugares reales y bonitos que poca gente explora.",
      fr: "Répondez à 3 questions rapides et nous vous suggérons de vrais beaux endroits peu explorés.",
      it: "Rispondi a 3 domande rapide e ti suggeriamo posti reali e belli che pochi esplorano.",
      de: "Beantworte 3 kurze Fragen und wir schlagen dir echte, schöne Orte vor, die wenige erkunden.",
      ja: "3つの質問に答えるだけで、あまり知られていない美しい実在の場所を提案します。",
    }),
    daysQuestion: pick({
      pt: "Quantos dias tens?",
      en: "How many days do you have?",
      es: "¿Cuántos días tienes?",
      fr: "Combien de jours avez-vous ?",
      it: "Quanti giorni hai?",
      de: "Wie viele Tage hast du?",
      ja: "何日間ありますか？",
    }),
    vibeQuestion: pick({
      pt: "Que ambiente procuras?",
      en: "What vibe are you after?",
      es: "¿Qué ambiente buscas?",
      fr: "Quelle ambiance cherchez-vous ?",
      it: "Che atmosfera cerchi?",
      de: "Welche Stimmung suchst du?",
      ja: "どんな雰囲気をお探しですか？",
    }),
    budgetQuestion: pick({
      pt: "Orçamento?",
      en: "Budget?",
      es: "¿Presupuesto?",
      fr: "Budget ?",
      it: "Budget?",
      de: "Budget?",
      ja: "予算は？",
    }),
    anyOption: pick({
      pt: "Tanto faz", en: "Anything", es: "Cualquiera", fr: "Peu importe", it: "Qualsiasi", de: "Egal", ja: "なんでも",
    }),
    daysOptions: DAYS.map(({ days, ...labels }) => ({ days, label: pick<string>(labels) })),
    vibeOptions: [
      { vibe: "nature", label: pick({ pt: "Natureza", en: "Nature", es: "Naturaleza", fr: "Nature", it: "Natura", de: "Natur", ja: "自然" }) },
      { vibe: "beach", label: pick({ pt: "Praia", en: "Beach", es: "Playa", fr: "Plage", it: "Mare", de: "Strand", ja: "ビーチ" }) },
      { vibe: "city", label: pick({ pt: "Cidade e cultura", en: "City & culture", es: "Ciudad y cultura", fr: "Ville et culture", it: "Città e cultura", de: "Stadt & Kultur", ja: "街と文化" }) },
      { vibe: "adventure", label: pick({ pt: "Aventura", en: "Adventure", es: "Aventura", fr: "Aventure", it: "Avventura", de: "Abenteuer", ja: "アドベンチャー" }) },
      { vibe: "relax", label: pick({ pt: "Descanso", en: "Relaxation", es: "Descanso", fr: "Détente", it: "Relax", de: "Erholung", ja: "リラックス" }) },
    ],
    budgetOptions: [
      { budget: "low", label: pick({ pt: "Económico", en: "Budget", es: "Económico", fr: "Économique", it: "Economico", de: "Günstig", ja: "低予算" }) },
      { budget: "medium", label: pick({ pt: "Médio", en: "Mid-range", es: "Medio", fr: "Moyen", it: "Medio", de: "Mittel", ja: "中予算" }) },
      { budget: "high", label: pick({ pt: "Confortável", en: "Comfort", es: "Confortable", fr: "Confort", it: "Comfort", de: "Komfort", ja: "高め" }) },
    ],
    suggestButton: pick({
      pt: "Sugerir destinos (1 crédito)",
      en: "Suggest destinations (1 credit)",
      es: "Sugerir destinos (1 crédito)",
      fr: "Suggérer des destinations (1 crédit)",
      it: "Suggerisci destinazioni (1 credito)",
      de: "Reiseziele vorschlagen (1 Guthaben)",
      ja: "目的地を提案（1クレジット）",
    }),
    loading: pick({
      pt: "A procurar destinos…",
      en: "Looking for destinations…",
      es: "Buscando destinos…",
      fr: "Recherche de destinations…",
      it: "Ricerca destinazioni…",
      de: "Suche Reiseziele…",
      ja: "目的地を検索中…",
    }),
    noResults: pick({
      pt: "Não consegui confirmar destinos agora. Não gastaste crédito — tenta outra vez.",
      en: "I couldn't confirm destinations right now. No credit was used — try again.",
      es: "No pude confirmar destinos ahora. No se usó crédito — inténtalo de nuevo.",
      fr: "Impossible de confirmer des destinations pour l'instant. Aucun crédit utilisé — réessayez.",
      it: "Non sono riuscito a confermare destinazioni ora. Nessun credito usato — riprova.",
      de: "Konnte gerade keine Reiseziele bestätigen. Kein Guthaben verbraucht — versuch es erneut.",
      ja: "目的地を確認できませんでした。クレジットは使用されていません — もう一度お試しください。",
    }),
    dismiss: pick({ pt: "Fechar", en: "Close", es: "Cerrar", fr: "Fermer", it: "Chiudi", de: "Schließen", ja: "閉じる" }),
  };
}
